// CENTRAL GALARYA — Consola V3 (ruta /consola-v3). Central operativa oficial.
// Consume datos REALES de preview (GET /api/alarmas) o un ESCENARIO DEMO (frontend-only, jamás al backend).
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getAlarmas, textoListaIncompleta } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useScenario } from "@/context/ScenarioContext";
import { useAlerts } from "@/context/AlertsContext";
import { useTheme } from "@/context/ThemeContext";
import TopBarV3 from "@/components/consolaV3/TopBarV3";
import QueueV3 from "@/components/consolaV3/QueueV3";
import CaseDetailV3 from "@/components/consolaV3/CaseDetailV3";
import ContextPanelV3 from "@/components/consolaV3/ContextPanelV3";
import CloseAlarmModal from "@/components/consola/CloseAlarmModal";
import PersonaDrawer from "@/components/persona/PersonaDrawer";
import PersonaQuickSearch from "@/components/persona/PersonaQuickSearch";
import { esTecnica } from "@/lib/consolaV3/meta";
import { componerActividadInstitucional, ACTIVIDAD_LIMITE } from "@/lib/consolaV3/actividadInstitucional";
import { crearRegistroOperaciones, claveOperacion, clasificarFallo, RESULTADO } from "@/lib/consolaV3/operaciones";
import { fmtTimestamp } from "@/lib/format";
import { contarTomadas } from "@/lib/consolaLib";
import { ShieldCheck, ListChecks, PanelLeft, PanelRight, X } from "lucide-react";

const useNow = () => { const [n, s] = useState(Date.now()); useEffect(() => { const t = setInterval(() => s(Date.now()), 1000); return () => clearInterval(t); }, []); return n; };

// UI-01E · con casos activos y ninguno seleccionado, el escudo cian y «Operación normal» sugerían
// ausencia de alarmas. Ahora el panel central ADVIERTE que hay cola pendiente y mantiene visibles
// las prioridades. No autoselecciona, no toma propiedad del caso y no abre expedientes: la
// monitorista sigue decidiendo qué caso atender.
function CalmEmpty({ live, hayCasos, demoMode, counts }) {
  const pend = hayCasos;
  return (
    <div data-testid="v3-calm-empty" data-estado={pend ? "pendientes" : "tranquilo"}
      className="gv2-dock-space flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      {pend
        ? <ListChecks className="h-12 w-12 gv2-alert-text" strokeWidth={1.9} />
        : <ShieldCheck className="h-12 w-12 gv2-ok" strokeWidth={1.9} />}
      <p className={`text-lg ${pend ? "font-semibold gv2-alert-text" : "font-semibold gv2-strong-text"}`}>
        {pend ? "Hay casos pendientes de atención" : "Operación normal"}
      </p>
      <p className="gv2-muted-text text-sm">{pend ? "Selecciona uno de la cola para ver el detalle y tomarlo." : "Sin alarmas ni incidencias pendientes."}</p>
      {pend && counts && (
        <div data-testid="v3-calm-empty-prioridades" className="flex flex-wrap items-center justify-center gap-2">
          <span className="gv2-chip gv2-chip-alert inline-flex items-center">{counts.altaSinTomar} ALTA sin tomar</span>
          <span className="gv2-chip gv2-chip-amber inline-flex items-center">{counts.mediaSinTomar} MEDIA sin tomar</span>
          <span className="gv2-chip gv2-chip-action inline-flex items-center">{counts.enAtencion} en atención</span>
        </div>
      )}
      {demoMode ? (
        <span className="gv2-chip gv2-chip-alert mt-2 inline-flex items-center">
          <span className="h-2 w-2 rounded-full gv2-dot-alert" /> Demostración · datos ficticios
        </span>
      ) : (
        <span className={`gv2-chip mt-2 inline-flex items-center ${live ? "gv2-chip-live" : "gv2-chip-alert"}`}>
          <span className={`h-2 w-2 rounded-full ${live ? "gv2-dot-ok" : "gv2-dot-alert"}`} /> {live ? "Sistema conectado" : "Sin señal"}
        </span>
      )}
    </div>
  );
}

export default function ConsolaV3() {
  const nowMs = useNow();
  const { user, isSupervisor, isExternal } = useAuth();
  const { scenario, demo, isDemo, demoTake, demoTransition, demoLiberar, demoCerrar, demoNota,
    demoAccion, demoDesenlace, demoGestion, demoSeguimiento } = useScenario();
  const { audioEnabled, enableAudio, disableAudio, testSound, audioState, resumeAudio, testSoundOnce } = useAlerts();
  const [searchParams, setSearchParams] = useSearchParams();
  // v2.15.2 — el tema es GLOBAL (ThemeContext): Central ya no es su dueña ni lo retira al salir.
  const { noche } = useTheme();
  const [alarmasReal, setAlarmasReal] = useState([]);
  const [actividadReal, setActividadReal] = useState([]);
  const [detailReal, setDetailReal] = useState(null);
  const [loadErrorReal, setLoadErrorReal] = useState(false);
  const [listaParcialReal, setListaParcialReal] = useState("");  // "" = cola recorrida entera
  const [lastOk, setLastOk] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [colaOpen, setColaOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const [cfg, setCfg] = useState({ max_alta_tomadas: 1, max_media_tomadas: 3 });
  const [motivosLib, setMotivosLib] = useState([]);
  const [notas, setNotas] = useState({});
  const [pDrawer, setPDrawer] = useState(null);
  const selRef = useRef(null); selRef.current = selectedId;
  // ATN-01 · M6 — operaciones pendientes y BORRADORES efímeros, separados por caso + actor y, dentro
  // de cada uno, por formulario. Nada se persiste: sin localStorage, sin IndexedDB y sin cola.
  const opsRef = useRef(crearRegistroOperaciones());
  // REPARACIÓN M6-A — operaciones SIN resolución acreditada, visibles para que el humano decida
  // reintentar el MISMO registro o descartarlo. REPARACIÓN M6-B — lectura posterior fallida.
  const [opsPend, setOpsPend] = useState({});
  const [lecturaPendiente, setLecturaPendiente] = useState(false);
  const [drafts, setDrafts] = useState({});
  const ticketRef = useRef(0);
  // ATN-01 · M4 — action_id del INICIO de llamada registrado por este actor en este caso.
  const intentosRef = useRef({});
  const uidRef = useRef(null);

  // H4 · `/config-operacion` es INTERNA: no se pide con una cuenta external (403 seguro).
  useEffect(() => { if (isDemo || user?.actor_type === "external") return; api.get("/config-operacion").then(({ data }) => setCfg(data)).catch(() => {}); }, [isDemo, user]);
  useEffect(() => { if (isDemo) return; api.get("/catalogos").then(({ data }) => setMotivosLib(data.motivos_liberacion || [])).catch(() => {}); }, [isDemo]);

  // Cambiar de escenario reinicia la selección. El estado demo mutable vive en ScenarioContext.
  useEffect(() => {
    if (isDemo) { setSelectedId(demo?.selectedId || null); setDetailReal(null); }
    else { setSelectedId(null); setDetailReal(null); }
  }, [scenario]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Datos reales (polling) ---
  const loadList = useCallback(async () => {
    const myTicket = (ticketRef.current += 1);
    try {
      const data = await getAlarmas();
      if (myTicket !== ticketRef.current) return;   // respuesta vieja: no pisa una más reciente
      setAlarmasReal(data); setLoadErrorReal(false); setLastOk(Date.now());
      setListaParcialReal(data?.incompleta ? textoListaIncompleta(data) : "");
    } catch { if (myTicket === ticketRef.current) setLoadErrorReal(true); }
  }, []);
  // v2.19.8 — la cuenta institucional no puede leer `/actividad-reciente` (internal-only en el
  // registro de rutas) y por eso el panel quedaba vacío. Ahora toma los MISMOS hechos del
  // read-model que sí tiene autorizado y con su alcance: `bitacora-operativa-1`. La cuenta interna
  // conserva exactamente su fuente anterior.
  const loadActividad = useCallback(async () => {
    try {
      if (isExternal) {
        const [evs, cas] = await Promise.all([
          api.get("/bitacora-operativa", { params: { clase: "eventos", limite: ACTIVIDAD_LIMITE } }),
          api.get("/bitacora-operativa", { params: { clase: "casos", limite: 10 } }),
        ]);
        setActividadReal(componerActividadInstitucional({
          eventos: evs.data?.items || [], casos: cas.data?.items || [],
        }));
        return;
      }
      const { data } = await api.get("/actividad-reciente", { params: { limit: ACTIVIDAD_LIMITE } });
      setActividadReal(data.eventos || []);
    } catch { /* informativo: si falla no afecta la operación de la cola */ }
  }, [isExternal]);
  // REPARACIÓN M6-B — la lectura devuelve un RESULTADO EXPLÍCITO en vez de absorber el error:
  //   {ok:true,data} · {ok:false,autorizacion:true} (RBAC: se retira el contenido) ·
  //   {ok:false,transitorio:true} (se CONSERVA el último detalle válido y se señala su estado).
  // Nunca dispara una segunda mutación: el reintento de lectura es sólo lectura.
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetailReal(null); setLecturaPendiente(false); return { ok: true, vacio: true }; }
    try {
      const { data } = await api.get(`/alarmas/${id}`);
      if (selRef.current === id) { setDetailReal(data); setLecturaPendiente(false); }
      return { ok: true, data };
    } catch (e) {
      const st = e?.response?.status;
      if (st === 401 || st === 403 || st === 404) {
        // Pérdida REAL de autorización (o caso inexistente): el contenido se retira.
        if (selRef.current === id) { setDetailReal(null); setLecturaPendiente(false); }
        return { ok: false, autorizacion: true, status: st };
      }
      if (selRef.current === id) setLecturaPendiente(true);
      return { ok: false, transitorio: true, status: st };
    }
  }, []);
  // v2.13.5 (PERF) — refresco posterior a una mutación del caso. Las dos lecturas son INDEPENDIENTES
  // entre sí: se disparan a la vez y sólo se ESPERA el detalle, que es lo que devuelve la operabilidad
  // del caso (ownership, siguiente acción, capacidades). La cola (GET /alarmas ~650 KB) ya no se
  // interpone en el camino crítico; se actualiza en cuanto llega y el polling la reconcilia igual.
  // No cambia qué se pide ni qué se muestra: sólo el orden de espera.
  const refrescarCaso = useCallback(async (id) => { loadList(); return await loadDetail(id); }, [loadList, loadDetail]);
  useEffect(() => {
    if (isDemo) return;
    loadList();
    loadActividad();
    const t = setInterval(() => { loadList(); loadActividad(); if (selRef.current) loadDetail(selRef.current); }, 2000);
    return () => clearInterval(t);
  }, [isDemo, loadList, loadActividad, loadDetail]);

  // Deep-link "Ir al caso" desde la capa global de alertas (?caso=<id>). En DEMO abre el caso demo
  // LOCAL (nunca envía el id al backend); en REAL consulta el detalle autoritativo.
  useEffect(() => {
    const caso = searchParams.get("caso");
    if (!caso) return;
    if (isDemo) {
      if (demo?.detalles?.[caso]) setSelectedId(caso);
      else toast.info("El caso demo ya no está disponible.");
      const p = new URLSearchParams(searchParams); p.delete("caso"); setSearchParams(p, { replace: true });
      return;
    }
    setSelectedId(caso);
    (async () => {
      try {
        const { data } = await api.get(`/alarmas/${caso}`);
        if (selRef.current !== caso) return;
        setDetailReal(data);
        if (data.estado === "cerrada") toast.info("El caso ya fue cerrado.");
      } catch {
        if (selRef.current === caso) { toast.info("El caso ya no está disponible."); setSelectedId(null); setDetailReal(null); }
      }
    })();
    const p = new URLSearchParams(searchParams); p.delete("caso"); setSearchParams(p, { replace: true });
  }, [searchParams, isDemo]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Vista unificada ---
  const alarmas = isDemo ? (demo?.alarmas || []) : alarmasReal;
  const actividad = isDemo
    ? ((demo?.eventos || []).filter((e) => (e.prioridad === "baja") && !e.alarma_id).slice(0, 5))
    : actividadReal;
  const loadError = isDemo ? false : loadErrorReal;
  const listaParcial = isDemo ? "" : listaParcialReal;
  const detail = isDemo ? (selectedId ? demo?.detalles?.[selectedId] : null) : detailReal;
  const live = isDemo ? true : (!loadErrorReal && Date.now() - lastOk < 7000);
  const motivosLibView = isDemo ? ["handoff", "fin_turno", "toma_erronea", "otro"] : motivosLib;

  const uid = isDemo ? "__me__" : user?.id;
  // ATN-01 · M6.7 — logout, CAMBIO DE ACTOR o 401: el contenido privado y el estado efímero se
  // retiran. Una respuesta tardía de la sesión terminada ya no puede restaurar nada.
  useEffect(() => {
    if (uidRef.current !== null && uidRef.current !== uid) {
      opsRef.current.limpiarTodo();
      setDrafts({});
      setOpsPend({});
      setLecturaPendiente(false);
      intentosRef.current = {};
      setDetailReal(null);
      setSelectedId(null);
    }
    uidRef.current = uid;
  }, [uid]);
  const marcarPendiente = useCallback((op) => setOpsPend((p) => ({
    ...p, [op.clave]: { clave: op.clave, control: op.control, casoId: op.casoId,
                        op_id: op.op_id, intentos: op.intentos || 1 },
  })), []);
  const quitarPendiente = useCallback((clave) => setOpsPend((p) => {
    const n = { ...p }; delete n[clave]; return n;
  }), []);
  const draftApi = useMemo(() => ({    get: (casoId, form) => drafts[`${casoId}::${uid}::${form}`] ?? "",
    set: (casoId, form, v) => setDrafts((p) => ({ ...p, [`${casoId}::${uid}::${form}`]: v })),
    clear: (casoId, form) => setDrafts((p) => {
      const n = { ...p }; delete n[`${casoId}::${uid}::${form}`]; return n;
    }),
  }), [drafts, uid]);
  // Advertencia ANTES de descartar texto no guardado. Tras una recarga sólo se recupera lo
  // persistido: no se promete recuperación de la memoria.
  useEffect(() => {
    const sucio = Object.values(drafts).some((v) => (v || "").trim());
    if (!sucio) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [drafts]);
  const held = contarTomadas(alarmas, uid);
  const takeBlockedAlta = !!detail && !esTecnica(detail) && detail.estado === "recibida" && detail.prioridad === "alta" && held.alta >= (cfg.max_alta_tomadas || 1);
  const notaActual = selectedId ? (notas[selectedId] ?? localStorage.getItem(`galarya_notav3_${selectedId}`) ?? "") : "";

  const counts = useMemo(() => {
    const recibidas = alarmas.filter((a) => a.estado === "recibida");
    return {
      altaSinTomar: recibidas.filter((a) => (a.prioridad === "maxima" ? "alta" : a.prioridad) === "alta" && !esTecnica(a)).length,
      mediaSinTomar: recibidas.filter((a) => a.prioridad === "media" && !esTecnica(a)).length,
      enAtencion: alarmas.filter((a) => a.estado !== "cerrada" && a.estado !== "recibida").length,
      coberturaCritica: alarmas.filter((a) => a.tipo === "cobertura_perdida" && a.estado === "recibida").length,
    };
  }, [alarmas]);

  const onSelect = (id) => { setSelectedId(id); if (!isDemo) loadDetail(id); setColaOpen(false); };

  const reloj = useMemo(() => {
    const d = new Date(nowMs);
    const hora = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Tijuana", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }).format(d);
    const fecha = new Intl.DateTimeFormat("es-MX", { timeZone: "America/Tijuana", weekday: "short", day: "2-digit", month: "short" }).format(d);
    return `${fecha} · ${hora} · Tijuana`;
  }, [nowMs]);

  const toggleAudio = () => { if (audioEnabled) disableAudio(); else enableAudio(); };
  // «Probar sonido» desde el gesto del operador:
  //  · silenciado → un único tono, sin tocar la preferencia ni arrancar el loop de alarmas;
  //  · bloqueado  → primero intenta reanudar el contexto y sólo entonces prueba los patrones;
  //  · activado   → conserva la prueba de los patrones ALTA/MEDIA.
  const probarSonido = async () => {
    if (!audioEnabled) { await testSoundOnce(); return; }
    if (audioState === "blocked") { const ok = await resumeAudio(); if (ok) testSound(); return; }
    testSound();
  };

  // --- Acciones ---
  const doTake = async (sobrecarga) => {
    if (!selectedId) return;
    if (isDemo) { demoTake(selectedId); toast.success("Demostración: caso tomado (registrado localmente, sin escribir al backend)."); return; }
    const tecSel = esTecnica(alarmas.find((x) => x.id === selectedId));
    setBusy(true);
    try { await api.post(`/alarmas/${selectedId}/tomar`, { sobrecarga }); toast.success(tecSel ? "Incidencia tomada" : "Alarma tomada"); await refrescarCaso(selectedId); }
    catch (e) { const d = e?.response?.data?.detail; if (e?.response?.status === 409 && d?.motivo) toast.error(d.mensaje || "No puedes tomar otra ALTA ahora."); else if (e?.response?.status === 409) toast.error(`Ya la tomó ${d?.operador || "otro operador"}.`); else toast.error(formatApiErrorDetail(d)); await loadList(); }
    finally { setBusy(false); }
  };
  const onTake = () => doTake(false);
  const onAtenderTambien = () => doTake(true);
  const onTransition = async (estado) => {
    if (isDemo) { demoTransition(selectedId, estado); return; }
    setBusy(true);
    try { await api.post(`/alarmas/${selectedId}/transicion`, { estado }); await refrescarCaso(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // ATN-01 · M6.4 — CLASE B (mutaciones sin idempotencia existente): ante incertidumbre se conserva
  // el texto, NO se autorreintenta y se remite a la secuencia. El reintento es decisión humana.
  const onNotaGestion = async (texto) => {
    if (!texto || !texto.trim()) return { estado: RESULTADO.RECHAZADO };
    if (isDemo) { demoNota(selectedId, texto); return { estado: RESULTADO.CONFIRMADO }; }
    const casoId = selectedId;
    const clave = claveOperacion(casoId, uid, "nota_gestion");
    const { nuevo, enVuelo } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control: "nota_gestion", payload: { texto } });
    if (enVuelo || !nuevo) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId };
    setBusy(true);
    try {
      await api.post(`/alarmas/${casoId}/nota`, { texto });
      opsRef.current.resolver(clave);
      toast.success("Nota registrada");
      const lec = await loadDetail(casoId);
      return { estado: lec.ok ? RESULTADO.CONFIRMADO : RESULTADO.CONFIRMADO_REFRESCO_PENDIENTE, casoId };
    } catch (e) {
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) {
        opsRef.current.resolver(clave);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        return { estado: RESULTADO.RECHAZADO, casoId };
      }
      opsRef.current.dejarPendiente(clave);
      await loadDetail(casoId);
      toast.error("No se pudo confirmar el registro. Revisa la secuencia antes de reintentar.");
      return { estado: RESULTADO.INCIERTO, casoId };
    } finally { setBusy(false); }
  };
  // OPS-01 §10 — el protocolo dejó de marcarse a mano en la operación normal (era captura
  // duplicada de lo que la monitorista ya registró como acción/resultado). El endpoint
  // `/alarmas/{id}/pasos/{orden}/ejecutar` del backend NO se toca ni se elimina.
  const onLiberar = async (motivoCodigo) => {
    if (isDemo) { demoLiberar(selectedId, motivoCodigo); toast.success("Demostración: caso liberado localmente."); return; }
    const tecSel = esTecnica(alarmas.find((x) => x.id === selectedId));
    setBusy(true);
    try { await api.post(`/alarmas/${selectedId}/liberar`, { motivo_codigo: motivoCodigo || "otro" }); toast.success(tecSel ? "Incidencia liberada" : "Alarma liberada"); setSelectedId(null); setDetailReal(null); await loadList(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onSolicitarApoyo = async () => {
    // DEMO: queda constancia local de la solicitud (nota de gestión). No sale ninguna comunicación.
    if (isDemo) { demoNota(selectedId, "Apoyo solicitado a supervisión para atender una segunda alarma ALTA."); toast.success("Demostración: apoyo registrado localmente."); return; }
    setBusy(true);
    // Idempotency-Key por clic: un reintento de red del mismo clic no crea una segunda solicitud.
    const idem = (window.crypto?.randomUUID?.() || `apoyo-${Date.now()}-${Math.random()}`);
    try { const { data } = await api.post(`/alarmas/${selectedId}/solicitar-apoyo`, null, { headers: { "Idempotency-Key": idem } }); const g = data.guardia || {}; toast.success(g.telefono ? `Apoyo registrado. Guardia: ${g.nombre || ""} ${g.telefono}` : "Apoyo registrado"); await loadDetail(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // REPARACIÓN M6-C · CLASE B (sin idempotencia acreditada): el resultado viaja EXPLÍCITO al modal.
  // Ni se autorreintenta, ni se cierra el formulario sin confirmación. El caso de origen viaja en
  // la respuesta para que el cierre visual se aplique SÓLO a ese caso/contacto.
  const onRegistrarGestion = async (contactoId, payload) => {
    if (isDemo) { demoGestion(selectedId, contactoId, payload); toast.success("Demostración: resultado de contacto registrado."); return { estado: RESULTADO.CONFIRMADO, casoId: selectedId, contactoId }; }
    const casoId = selectedId;
    const clave = claveOperacion(casoId, uid, `gestion:${contactoId}`);
    const { nuevo, enVuelo } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control: "gestion", payload: { contactoId, ...payload } });
    if (enVuelo || !nuevo) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId, contactoId };
    setBusy(true);
    try {
      await api.post(`/alarmas/${casoId}/gestion-contacto`, { contacto_id: contactoId, ...payload });
      opsRef.current.resolver(clave);
      toast.success("Resultado de contacto registrado");
      const lec = await loadDetail(casoId);
      return { estado: lec.ok ? RESULTADO.CONFIRMADO : RESULTADO.CONFIRMADO_REFRESCO_PENDIENTE,
               casoId, contactoId };
    } catch (e) {
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) {
        opsRef.current.resolver(clave);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        return { estado: RESULTADO.RECHAZADO, casoId, contactoId };
      }
      opsRef.current.dejarPendiente(clave);
      const lec = await loadDetail(casoId);
      toast.error("No se pudo confirmar el registro. Revisa la secuencia antes de reintentar.");
      return { estado: RESULTADO.INCIERTO, casoId, contactoId,
               autorizacion: lec.autorizacion === true };
    } finally { setBusy(false); }
  };
  // v2.9.2 — cableado del ciclo Día 1 desde la consola (endpoints ya existentes).
  // REPARACIÓN M6-A/E · CLASE A (journal con idempotencia existente).
  // Una operación incierta NO se suelta: conserva op_id, caso, actor, control y payload inmutable.
  // El reintento manual reconcilia primero por GET y, si sigue sin confirmación, reenvía con la
  // MISMA Idempotency-Key y el MISMO payload. El escritor y su idempotencia no se tocan.
  const reconciliarAccion = async (casoId, op) => {
    const p = op.payload || {};
    const { data } = await api.get(`/alarmas/${casoId}`);
    const hecho = (data.journal || []).find(
      (x) => (x.metadata || {}).op_id === op.op_id && x.kind === p.kind
        && (x.objetivo || null) === (p.objetivo || null));
    return hecho ? { data, hecho } : { data, hecho: null };
  };

  const enviarAccion = async (casoId, op, { silencioso = false } = {}) => {
    const p = op.payload || {};
    const cuerpo = { ...p, metadata: { ...(p.metadata || {}), op_id: op.op_id } };
    try {
      const { data } = await api.post(`/casos/${casoId}/acciones`, cuerpo,
        { headers: { "Idempotency-Key": op.op_id } });
      opsRef.current.resolver(op.clave);
      quitarPendiente(op.clave);
      if (!silencioso) toast.success("Registrado");
      const lec = await refrescarCaso(casoId);
      if (!lec.ok && lec.transitorio) {
        toast.info("Guardado; actualización pendiente.");
        return { estado: RESULTADO.CONFIRMADO_REFRESCO_PENDIENTE, op_id: op.op_id,
                 action_id: data?.action_id || null, casoId };
      }
      return { estado: RESULTADO.CONFIRMADO, op_id: op.op_id,
               action_id: data?.action_id || null, casoId };
    } catch (e) {
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) {
        opsRef.current.resolver(op.clave);
        quitarPendiente(op.clave);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        return { estado: RESULTADO.RECHAZADO, casoId };
      }
      // INCIERTO: se reconcilia LEYENDO el mismo caso por op_id. La ausencia en el GET no se
      // declara rechazo (el journal se lee acotado) y la identidad queda VIVA para reintentar.
      try {
        const { data, hecho } = await reconciliarAccion(casoId, op);
        if (hecho) {
          opsRef.current.resolver(op.clave);
          quitarPendiente(op.clave);
          if (selRef.current === casoId) { setDetailReal(data); setLecturaPendiente(false); }
          if (!silencioso) toast.success("Registrado");
          return { estado: RESULTADO.CONFIRMADO, op_id: op.op_id,
                   action_id: hecho.action_id, casoId };
        }
      } catch { /* la lectura de reconciliación también puede fallar: sigue INCIERTO */ }
      opsRef.current.dejarPendiente(op.clave);
      marcarPendiente(op);
      toast.error("No se pudo confirmar el registro. Revisa la secuencia antes de reintentar.");
      return { estado: RESULTADO.INCIERTO, op_id: op.op_id, casoId, pendiente: true };
    }
  };

  const onRegistrarAccion = async (payload, control = "accion") => {
    if (isDemo) { demoAccion(selectedId, payload); toast.success("Demostración: registrado en el caso."); return { estado: RESULTADO.CONFIRMADO }; }
    const casoId = selectedId;
    if (!casoId) return { estado: RESULTADO.RECHAZADO };
    const clave = claveOperacion(casoId, uid, control);
    const { nuevo, enVuelo, conflicto, op } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control, payload });
    if (enVuelo) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId };
    if (conflicto) {
      toast.error("Hay un registro anterior sin confirmar en este control. Reintenta ese mismo "
                  + "registro o descártalo antes de capturar otra atención.");
      marcarPendiente(op);
      return { estado: RESULTADO.INCIERTO, conflicto: true, casoId };
    }
    if (!nuevo) return { estado: RESULTADO.INCIERTO, casoId };
    setBusy(true);
    try { return await enviarAccion(casoId, op); }
    finally { setBusy(false); }
  };

  // Reintento MANUAL de una operación pendiente: misma clave, mismo payload. Nunca otro op_id.
  const reintentarOperacion = async (clave) => {
    const op = opsRef.current.pendiente(clave);
    if (!op || opsRef.current.enVuelo(clave)) return { estado: RESULTADO.INCIERTO };
    setBusy(true);
    try {
      const { hecho, data } = await reconciliarAccion(op.casoId, op);
      if (hecho) {
        opsRef.current.resolver(clave);
        quitarPendiente(clave);
        if (selRef.current === op.casoId) { setDetailReal(data); setLecturaPendiente(false); }
        toast.success("El registro ya constaba en el caso.");
        return { estado: RESULTADO.CONFIRMADO, action_id: hecho.action_id, casoId: op.casoId };
      }
    } catch { /* sin confirmación por lectura: se permite el reenvío idempotente */ }
    finally { setBusy(false); }
    const re = opsRef.current.adquirir(clave, { casoId: op.casoId, actorId: op.actorId,
                                                control: op.control, payload: op.payload });
    if (!re.nuevo) { setBusy(false); return { estado: RESULTADO.INCIERTO }; }
    setBusy(true);
    try { return await enviarAccion(op.casoId, re.op); }
    finally { setBusy(false); }
  };

  // Descarte SEGURO y EXPLÍCITO: lo decide el humano tras revisar la secuencia. Suelta la
  // identidad para que pueda capturarse una atención nueva.
  const descartarOperacion = (clave) => {
    opsRef.current.resolver(clave);
    quitarPendiente(clave);
    toast.info("Registro pendiente descartado. Revisa la secuencia antes de capturar de nuevo.");
  };

  // OPS-01 §6/§7 — EVIDENCIA AUTOMÁTICA del intento real: la consola registra que la llamada se
  // INICIÓ (actor y hora autoritativos del servidor). Iniciar NO es contacto efectivo: el resultado
  // humano se pide aparte. No hay telemetría de la llamada, así que no se afirma nada más.
  // REPARACIÓN M6-E — si la respuesta se pierde, la operación del inicio queda PENDIENTE con su
  // identidad: al volver a marcar se reconcilia y se recupera el action_id, sin registrar un
  // segundo inicio por el mismo intento. La llamada humana nunca se bloquea por el journal.
  const onIntentoLlamada = async ({ kind, telefono, objetivo }) => {
    if (isDemo || !selectedId) return null;
    const casoId = selectedId;
    const payload = { kind, objetivo: objetivo || null,
                      metadata: { fase: "iniciada", destino: telefono || null } };
    const clave = claveOperacion(casoId, uid, `intento:${kind}:${objetivo || "persona"}`);
    const { nuevo, enVuelo, op } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control: "intento", payload });
    if (enVuelo || !nuevo) return null;
    const r = await enviarAccion(casoId, op, { silencioso: true });
    if (r.action_id) intentosRef.current[`${casoId}::${uid}`] = r.action_id;
    return r.action_id || null;
  };
  // REPARACIÓN M6-D · CLASE C (transición de desenlace): el control espera el resultado antes de
  // limpiar su selección. Ante incertidumbre se consulta el desenlace PERSISTIDO y se muestra lo
  // realmente comprobado; no se reenvía una transición incierta. El backend no se toca.
  //
  // CORRECCIÓN D (Codex) — mientras la operación siga INCIERTA, su identidad (caso, actor, intención
  // y payload) permanece viva y una nueva pulsación CONSULTA primero el estado: nunca ejecuta otro
  // POST. La ausencia en la lectura NO demuestra que el POST fue rechazado, así que la incertidumbre
  // se conserva. Un desenlace registrado por otra persona se muestra, pero no se atribuye al actor.
  const acreditarDesenlace = async (op) => {
    const lec = await loadDetail(op.casoId);
    if (!lec.ok) return { conocido: false, lec };                 // GET fallido: sigue INCIERTO
    const vd = lec.data?.verificacion_desenlace || null;
    if (!vd?.codigo) return { conocido: true, vd: null, lec };    // ausencia ≠ rechazo
    const p = op.payload || {};
    const coincide = vd.codigo === p.codigo
      && (p.metodo ? vd.metodo === p.metodo : true)
      && (p.contacto_id ? vd.contacto_id === p.contacto_id : true);
    const propio = coincide && (!vd.por_id || !op.actorId || vd.por_id === op.actorId);
    return { conocido: true, vd, propio, lec };
  };

  const revisarPendienteDesenlace = async (clave, op) => {
    const r = await acreditarDesenlace(op);
    if (r.conocido && r.vd?.codigo) {
      opsRef.current.resolver(clave);
      quitarPendiente(clave);
      if (r.propio) {
        toast.success("El desenlace ya constaba registrado en el caso.");
        return { estado: RESULTADO.CONFIRMADO, casoId: op.casoId, reconciliado: true };
      }
      toast.info(`El caso ya tiene un desenlace registrado${r.vd.por ? ` por ${r.vd.por}` : ""}. Se muestra lo comprobado.`);
      return { estado: RESULTADO.CONFIRMADO, casoId: op.casoId, ajeno: true, reconciliado: true };
    }
    opsRef.current.dejarPendiente(clave);
    marcarPendiente(op);
    toast.error("El desenlace anterior sigue sin confirmarse. Usa «Revisar estado»: no se reenvía el registro a ciegas.");
    return { estado: RESULTADO.INCIERTO, casoId: op.casoId, pendiente: true,
             autorizacion: r.lec?.autorizacion === true };
  };

  // «Revisar estado» de un desenlace pendiente: SÓLO LECTURA, nunca repite la mutación.
  const revisarOperacionDesenlace = async (clave) => {
    const op = opsRef.current.pendiente(clave);
    if (!op || opsRef.current.enVuelo(clave)) return { estado: RESULTADO.INCIERTO };
    setBusy(true);
    try { return await revisarPendienteDesenlace(clave, op); }
    finally { setBusy(false); }
  };

  const onRegistrarDesenlace = async (payload) => {
    if (isDemo) { demoDesenlace(selectedId, payload); toast.success("Demostración: desenlace registrado en el caso."); return { estado: RESULTADO.CONFIRMADO, casoId: selectedId }; }
    const casoId = selectedId;
    if (!casoId) return { estado: RESULTADO.RECHAZADO };
    const clave = claveOperacion(casoId, uid, "desenlace");
    if (opsRef.current.enVuelo(clave)) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId };
    const prev = opsRef.current.pendiente(clave);
    if (prev) {
      // Operación incierta anterior sobre ESTE caso y actor: se consulta, no se reenvía.
      setBusy(true);
      try { return await revisarPendienteDesenlace(clave, prev); }
      finally { setBusy(false); }
    }
    const { nuevo, op } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control: "desenlace", payload });
    if (!nuevo) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId };
    setBusy(true);
    try {
      await api.post(`/casos/${casoId}/verificacion-desenlace`, payload);
      opsRef.current.resolver(clave);
      quitarPendiente(clave);
      toast.success("Desenlace registrado");
      const lec = await refrescarCaso(casoId);
      return { estado: lec.ok ? RESULTADO.CONFIRMADO : RESULTADO.CONFIRMADO_REFRESCO_PENDIENTE, casoId };
    } catch (e) {
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) {
        // Rechazo CONFIRMADO por la API: la identidad se suelta y el humano puede corregir y enviar.
        opsRef.current.resolver(clave);
        quitarPendiente(clave);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        return { estado: RESULTADO.RECHAZADO, casoId };
      }
      opsRef.current.dejarPendiente(clave);
      return await revisarPendienteDesenlace(clave, op);
    } finally { setBusy(false); }
  };
  const onCumplirWait = async (waitId) => {
    // Ningún fixture demo usa esperas estructuradas, por lo que este control no se renderiza en DEMO.
    if (isDemo) { toast.info("Demostración: sin esperas estructuradas en este escenario."); return; }
    setBusy(true);
    try { await api.post(`/waits/${waitId}/cumplir`); toast.success("Compromiso confirmado"); await loadDetail(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onRegistrarEscalamiento = async (payload) => {
    // INSTITUCIONAL: categoría operativa + hora + actor. LAB7 · P1-A: personal_atendio y
    // estado_apoyo viajan tal como los escribió el humano; nada se autocompleta.
    // ATN-01 · M6.4 — CLASE B: sin autorreintento y con resultado explícito para el formulario.
    if (isDemo) { toast.info("Demostración: el escalamiento interno se registra en el flujo real."); return { estado: RESULTADO.RECHAZADO }; }
    const casoId = selectedId;
    const clave = claveOperacion(casoId, uid, "escalamiento");
    const { nuevo, enVuelo } = opsRef.current.adquirir(
      clave, { casoId, actorId: uid, control: "escalamiento", payload });
    if (enVuelo || !nuevo) return { estado: RESULTADO.INCIERTO, en_curso: true, casoId };
    setBusy(true);
    try {
      await api.post(`/casos/${casoId}/escalamiento-interno`, payload);
      opsRef.current.resolver(clave);
      toast.success("Escalamiento interno registrado");
      const lec = await refrescarCaso(casoId);
      return { estado: lec.ok ? RESULTADO.CONFIRMADO : RESULTADO.CONFIRMADO_REFRESCO_PENDIENTE, casoId };
    } catch (e) {
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) {
        opsRef.current.resolver(clave);
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        return { estado: RESULTADO.RECHAZADO, casoId };
      }
      opsRef.current.dejarPendiente(clave);
      await loadDetail(casoId);
      toast.error("No se pudo confirmar el registro. Revisa la secuencia antes de reintentar.");
      return { estado: RESULTADO.INCIERTO, casoId };
    } finally { setBusy(false); }
  };
  const onIdentificarResidente = async (payload) => {
    // LAB7 · P1-B/C: habitación compartida. Deja constancia de la VERIFICACIÓN HUMANA; no cambia
    // la persona del caso, la cama ni la atribución del sensor.
    if (isDemo) { toast.info("Demostración: la identificación se registra en el flujo real."); return; }
    setBusy(true);
    try { await api.post(`/casos/${selectedId}/residente-identificado`, payload); toast.success("Residente identificado por verificación humana"); await refrescarCaso(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onResolverSeguimiento = async (payload) => {    if (isDemo) { demoSeguimiento(selectedId, payload); toast.success(payload.resultado === "cumplido" ? "Demostración: seguimiento cumplido." : "Demostración: seguimiento marcado no obtenible."); return; }
    setBusy(true);
    try { await api.post(`/casos/${selectedId}/emergencia/seguimiento`, payload); toast.success(payload.resultado === "cumplido" ? "Seguimiento cumplido" : "Seguimiento marcado no obtenible"); await refrescarCaso(selectedId); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onConfirmClose = async (payload) => {
    const tecSel = esTecnica(detail) || esTecnica(alarmas.find((x) => x.id === selectedId));
    // TECH-001: una clasificación NO terminal registra SEGUIMIENTO y la incidencia sigue abierta.
    // El modal técnico SIEMPRE declara `terminal`; el modal humano nunca lo envía.
    const soloSeguimiento = payload.terminal === false;
    if (isDemo) {
      if (soloSeguimiento) {
        demoNota(selectedId, `[${payload.clasificacion}] ${payload.nota_cierre}`);
        setCloseOpen(false);
        toast.success("Demostración: seguimiento técnico registrado. La incidencia sigue abierta.");
        return;
      }
      demoCerrar(selectedId, payload);
      setCloseOpen(false);
      // R55-patch (A): un caso CERRADO no puede seguir siendo el foco operativo. Se conserva en el
      // estado demo (historial/actividad, con actor, estado y cierre) pero el foco avanza a la
      // siguiente alerta DEMO activa; si no queda ninguna, se vuelve a "sin caso seleccionado".
      // Todo local: no se escribe al backend y el flujo REAL no se toca.
      const siguiente = alarmas.find((a) => a.id !== selectedId && a.estado !== "cerrada");
      setSelectedId(siguiente ? siguiente.id : null);
      toast.success(siguiente
        ? "Demostración: caso cerrado localmente. Pasamos al siguiente caso activo."
        : "Demostración: caso cerrado localmente. No quedan casos activos.");
      return;
    }
    const casoCierre = selectedId;
    setBusy(true);
    try {
      if (soloSeguimiento) {
        await api.post(`/alarmas/${casoCierre}/seguimiento-tecnico`, { clasificacion: payload.clasificacion, nota: payload.nota_cierre });
        toast.success("Seguimiento registrado. La incidencia sigue abierta.");
        if (selRef.current === casoCierre) setCloseOpen(false);
        await refrescarCaso(casoCierre);
        return;
      }
      await api.post(`/alarmas/${casoCierre}/cerrar`, payload);
      toast.success(tecSel ? "Incidencia cerrada" : "Alarma cerrada");
      localStorage.removeItem(`galarya_notav3_${casoCierre}`);
      setNotas((p) => { const n = { ...p }; delete n[casoCierre]; return n; });
      // REPARACIÓN M6-F — el cierre de A se REGISTRA siempre, pero el foco sólo se retira si el
      // caso cerrado SIGUE siendo el abierto: si la monitorista ya está en B, B no se toca.
      if (selRef.current === casoCierre) { setCloseOpen(false); setSelectedId(null); setDetailReal(null); }
      await loadList();
    }
    catch (e) {
      // ATN-01 · M6.5 — CLASE C: ante incertidumbre se CONSULTA el caso y se contrasta contra la
      // operación. Encontrar el caso cerrado NO demuestra que lo cerró el actor actual, y un GET que
      // todavía no muestra el efecto NO prueba rechazo.
      const clase = clasificarFallo(e);
      if (clase === RESULTADO.RECHAZADO) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); return; }
      try {
        const { data } = await api.get(`/alarmas/${casoCierre}`);
        if (data.estado === "cerrada") {
          const mismo = !!data.cierre_por && (data.cierre_por === user?.nombre || data.cierre_por === user?.email);
          if (mismo) {
            toast.success("El cierre quedó registrado.");
            if (selRef.current === casoCierre) { setCloseOpen(false); setSelectedId(null); setDetailReal(null); }
            await loadList();
            return;
          }
          toast.info(`El caso ya figura cerrado por ${data.cierre_por || "otro actor"}. No se atribuye a tu sesión.`);
          if (selRef.current === casoCierre) setDetailReal(data);
          return;
        }
      } catch { /* la lectura de reconciliación también puede fallar */ }
      toast.error("No se pudo confirmar el cierre. Revisa la secuencia antes de reintentar.");
    }
    finally { setBusy(false); }
  };

  const stale = !!loadError;

  // v2.9.2 — clasificación de cierre Día 1 derivada del estado autoritativo del caso (desenlace,
  // handoff o acción final documentada). Determina qué terminal válida usa el modal de cierre.
  const dia1Cierre = useMemo(() => {
    const d = detail;
    if (!d || d.es_tecnica) return null;   // técnicas -> modal legacy
    const inst = (d.modalidad_respuesta || d._modalidad) === "institucional";
    // v2.13.1 — RECORDATORIO PROGRAMADO: dos resoluciones propias y nota OPCIONAL (los intentos de
    // llamada y el WhatsApp manual ya quedaron registrados como hechos con actor y hora).
    if (d.tipo === "recordatorio_programado") {
      // ATN-01 · M3.4 — el cierre NO vuelve a pedir una resolución ya registrada: si consta un
      // hecho terminal, llega PRESELECCIONADO (editable y con confirmación humana). Sin hecho
      // terminal no se inventa preselección.
      const j = d.journal || [];
      const comunicado = j.some((x) => x.kind === "llamada_persona" && x.resultado_codigo === "recordatorio_comunicado");
      const wa = !comunicado && j.some((x) => x.kind === "whatsapp_manual");
      return { mode: "person", clasificacion: comunicado ? "recordatorio_comunicado" : wa ? "sin_contacto" : "",
               label: comunicado ? "Recordatorio comunicado" : wa ? "Sin contacto" : "", selectable: true,
               notaObligatoria: false, recordatorio: true, inst };
    }
    const des = (d.verificacion_desenlace || {}).codigo;
    const journal = d.journal || [];
    const evidencia = !!des || !!(d.emergencia || {}).handoff_completado_en
      || journal.some((x) => x.resultado_codigo) || (d.gestiones_contacto || []).length > 0
      || journal.some((x) => x.kind === "accion_final");
    // GOV-004: TODO cierre humano exige nota factual final (el backend es autoritativo y la rechaza
    // si viene vacía). El frontend refleja exactamente la misma regla: la nota ya no es "opcional".
    if (des === "VERIFICADO_DIRECTO") return { mode: "person", clasificacion: "verificado_directamente", label: inst ? "Atención verificada por el personal del centro" : "Verificado directamente", selectable: false, notaObligatoria: true, inst };
    if (des === "VERIFICADO_CONTACTO_AUTORIZADO") return { mode: "person", clasificacion: "verificado_contacto_autorizado", label: inst ? "Confirmado por un contacto autorizado (aviso secundario)" : "Verificado por contacto autorizado", selectable: false, notaObligatoria: true, inst };
    if ((d.emergencia || {}).handoff_completado_en) return { mode: "person", clasificacion: "emergencia_handoff_completado", label: "Handoff a emergencias completado", selectable: false, notaObligatoria: true, inst };
    const hechoFinal = journal.some((x) => x.kind === "accion_final");
    // ATN-01 · M5 — «Escalado internamente» SÓLO cuando existe un escalamiento PERSISTIDO. En otro
    // caso la gestión del centro se nombra por lo que consta, sin afirmar un escalamiento.
    const hayEscalamiento = (d.escalamientos_internos || []).length > 0;
    if (hechoFinal || des === "NO_VERIFICABLE") return { mode: "person", clasificacion: "no_verificado_accion_final_cumplida", label: inst ? (hayEscalamiento ? "Escalado internamente · gestión del centro documentada" : "Gestión del centro documentada") : "No verificado — acción final cumplida", selectable: false, notaObligatoria: true, inst };
    // Caso B — cierre directo sin terminal precargada: selector de resolución Día 1.
    return { mode: "person", clasificacion: "", label: "", selectable: true, notaObligatoria: true, sinEvidencia: !evidencia, inst };
  }, [detail]);

  // ATN-01 · M5 — notas YA registradas en ESTE caso, con su origen, actor y hora. Fuentes
  // autorizadas: `journal[].resultado_texto_factual` y los `journal_nota` de la secuencia. No se
  // publica `notas_gestion` crudo ni se abre otra consulta para este selector.
  const notasPrevias = useMemo(() => {
    const d = detail;
    if (!d) return [];
    const out = [];
    (d.journal || []).forEach((x, i) => {
      const t = (x.resultado_texto_factual || "").trim();
      if (t) out.push({ id: x.action_id || `hecho-${i}`, texto: t, actor: x.actor,
                        hora: fmtTimestamp(x.created_at), origen: "Hecho del caso" });
    });
    (d.secuencia_combinada || []).forEach((it, i) => {
      if (it.tipo !== "journal_nota") return;
      const t = (it.detalle || "").trim();
      if (t) out.push({ id: `nota-${i}`, texto: t, actor: it.actor,
                        hora: fmtTimestamp(it.hora), origen: "Nota de la gestión" });
    });
    return out;
  }, [detail]);

  // Búsqueda global de persona (padrón). No reemplaza la cola; abre la Persona consolidada.
  const centralSource = async (query) => {
    if (isDemo) {
      const base = demo?.poblacion || [];
      return base.filter((t) => t.nombre.toLowerCase().includes(query.toLowerCase())).map((t) => ({ id: t.id, nombre: t.nombre, edad: t.edad, sitio_nombre: t.sitio_nombre, caso: !!t.atencion }));
    }
    const { data } = await api.get("/personas", { params: { q: query } });
    return (data.personas || []).map((p) => ({ id: p.id, nombre: p.nombre, edad: p.edad, sitio_nombre: p.sitio_nombre, caso: !!p.caso_activo }));
  };
  const onOpenPersona = (r) => setPDrawer({ id: r.id, demoFicha: isDemo ? (demo?.fichas?.[r.id] || null) : null });

  return (
    <div className={`${noche ? "dark" : ""} h-full`}>
      <div data-testid="consola-v3-page" className="gv2 tema-noche-auto h-full flex flex-col bg-[#F1F5F9] text-slate-800">
        <TopBarV3 reloj={reloj} live={live} demoMode={isDemo} counts={counts} audioState={audioState} onToggleAudio={toggleAudio}
          onTestSound={probarSonido} />

        <div className="shrink-0 flex items-center gap-2 border-b border-slate-200 bg-white/70 px-3 py-1.5 sm:px-4">
          <span className="hidden shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400 sm:inline">Buscar persona</span>
          <div className="min-w-0 flex-1 sm:ml-auto sm:flex-none"><PersonaQuickSearch source={centralSource} onOpen={onOpenPersona} resetKey={scenario} placeholder="Buscar persona por nombre o apellido" /></div>
        </div>

        {isDemo && (
          <div data-testid="v3-demo-banner" className="shrink-0 flex flex-wrap items-center gap-2 border-b border-[#9B2C2C]/30 bg-[#9B2C2C]/10 dark:bg-[#9B2C2C]/25 px-4 py-1.5 text-xs font-semibold text-[#9B2C2C] dark:text-[#F2B8B8]">
            DEMOSTRACIÓN · DATOS FICTICIOS · NO SE MUESTRA MONITOREO REAL — {demo.label} · {demo.resumen}
          </div>
        )}

        {listaParcial && (
          <div data-testid="v3-lista-incompleta" className="shrink-0 bg-amber-500 px-4 py-1.5 text-center text-sm font-semibold text-white">
            LISTA INCOMPLETA — {listaParcial}
          </div>
        )}

        {loadError && (
          <div data-testid="v3-conn-banner" className="shrink-0 bg-[#9B2C2C] px-4 py-1.5 text-center text-sm font-semibold text-white">
            SIN SEÑAL — mostrando la última información confirmada. Datos marcados como DATO DESACTUALIZADO.
          </div>
        )}

        <div className="flex-1 min-h-0 flex relative">
          <aside className={`${colaOpen ? "flex absolute z-30 inset-y-0 left-0 w-[85%] max-w-[380px] shadow-xl" : "hidden"} lg:flex lg:relative lg:w-[320px] xl:w-[380px] shrink-0 flex-col border-r border-slate-200 bg-[#F1F5F9]`}>
            <QueueV3 alarmas={alarmas} selectedId={selectedId} onSelect={onSelect} nowMs={nowMs} stale={stale} actividad={actividad} />
          </aside>
          {colaOpen && <div className="lg:hidden absolute inset-0 z-20 bg-black/40" onClick={() => setColaOpen(false)} />}

          <section className="flex-1 min-w-0 overflow-y-auto">
            <div data-testid="v3-subnav-movil" className="sticky top-0 z-20 flex xl:hidden items-center gap-1.5 border-b border-slate-200 bg-[#F1F5F9]/95 px-2.5 py-1.5 backdrop-blur">
              <button data-testid="v3-btn-cola" onClick={() => setColaOpen(true)} aria-label="Abrir la cola de casos" className="gv2-chip-btn inline-flex shrink-0 items-center lg:hidden">
                <PanelLeft className="h-4 w-4" /> Cola
              </button>
              {detail && <button data-testid="v3-btn-ctx" onClick={() => setCtxOpen(true)} aria-label="Abrir la ficha del residente" className="gv2-chip-btn inline-flex min-w-0 shrink items-center xl:hidden">
                <PanelRight className="h-4 w-4 shrink-0" /> <span className="truncate"><span className="sm:hidden">Ficha</span><span className="hidden sm:inline">Ficha del residente</span></span>
              </button>}
              {detail && <button data-testid="v3-btn-salir-caso" onClick={() => { setSelectedId(null); setDetailReal(null); }} aria-label="Salir del caso" className="gv2-chip-btn ml-auto inline-flex shrink-0 items-center">
                <X className="h-4 w-4" /> <span className="hidden sm:inline">Salir del caso</span><span className="sm:hidden">Salir</span>
              </button>}
            </div>
            {!detail ? (
              <CalmEmpty live={live} demoMode={isDemo} counts={counts} hayCasos={alarmas.some((a) => a.estado !== "cerrada")} />
            ) : (
              <div className="gv2-dock-space mx-auto w-full max-w-[1280px] p-3 sm:p-4 md:p-5">
                {/* REPARACIÓN M6-B — el POST quedó confirmado pero la LECTURA posterior falló: se
                    conserva el último detalle válido, se dice su estado y sólo se reintenta la
                    lectura. Nunca se repite la mutación. */}
                {lecturaPendiente && (
                  <div data-testid="v3-lectura-pendiente" className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs font-semibold text-amber-800 dark:text-amber-300">
                    <span>Guardado; actualización pendiente. Lo que ves puede no incluir el último registro.</span>
                    <button data-testid="v3-lectura-reintentar" disabled={busy}
                      onClick={() => loadDetail(selectedId)}
                      className="rounded-md border border-amber-500 px-2 py-1 font-bold text-amber-800 dark:text-amber-200">
                      Reintentar lectura
                    </button>
                  </div>
                )}
                {/* REPARACIÓN M6-A — registro SIN resolución acreditada: su identidad sigue viva.
                    El reintento reutiliza la misma clave de idempotencia y el mismo payload; el
                    descarte es una decisión humana explícita. */}
                {Object.values(opsPend).filter((o) => o.casoId === selectedId).map((o) => (
                  <div key={o.clave} data-testid={`v3-op-pendiente-${o.control}`} className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-rose-400/60 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-xs font-semibold text-rose-800 dark:text-rose-300">
                    {o.control === "desenlace" ? (
                      <>
                        {/* CORRECCIÓN D — el desenlace no tiene idempotencia en el escritor: aquí sólo
                            se COMPRUEBA lo registrado. Nunca se reenvía la transición a ciegas. */}
                        {/* FIX FINAL — en un desenlace incierto NO se ofrece «Descartar»: soltar la
                            identidad sin lectura acreditada permitiría un segundo registro. Sólo
                            queda la comprobación de estado, que es de sólo lectura. */}
                        <span>El desenlace de este caso quedó sin confirmar. Antes de volver a registrarlo se comprueba lo que consta.</span>
                        <button data-testid="v3-op-revisar" disabled={busy}
                          onClick={() => revisarOperacionDesenlace(o.clave)}
                          className="rounded-md border border-rose-500 px-2 py-1 font-bold">Revisar estado</button>
                      </>
                    ) : (
                      <>
                        <span>Hay un registro sin confirmar en este caso ({o.control}). Revisa la secuencia: puede haberse guardado.</span>
                        <button data-testid="v3-op-reintentar" disabled={busy}
                          onClick={() => reintentarOperacion(o.clave)}
                          className="rounded-md border border-rose-500 px-2 py-1 font-bold">Reintentar el mismo registro</button>
                        <button data-testid="v3-op-descartar" disabled={busy}
                          onClick={() => descartarOperacion(o.clave)}
                          className="rounded-md border border-slate-400 px-2 py-1 font-bold text-slate-600 dark:text-slate-300">Descartar</button>
                      </>
                    )}
                  </div>
                ))}
                <CaseDetailV3 detail={detail} nowMs={nowMs} busy={busy} demo={isDemo} motivosLiberacion={motivosLibView} isSupervisor={isSupervisor}
                  onTake={onTake} onCloseClick={() => setCloseOpen(true)} onLiberar={onLiberar} onSolicitarApoyo={onSolicitarApoyo}
                  onTransition={onTransition} onAtenderTambien={onAtenderTambien} takeBlockedAlta={takeBlockedAlta}
                  onNotaGestion={onNotaGestion} onRegistrarGestion={onRegistrarGestion} onCumplirWait={onCumplirWait}
                  onRegistrarAccion={onRegistrarAccion} onRegistrarDesenlace={onRegistrarDesenlace} onResolverSeguimiento={onResolverSeguimiento}
                  onRegistrarEscalamiento={onRegistrarEscalamiento} onIntentoLlamada={onIntentoLlamada}
                  onIdentificarResidente={onIdentificarResidente} drafts={draftApi} actorId={uid} />
              </div>
            )}
          </section>

          <aside data-testid="v3-context-aside" className={`${ctxOpen ? "flex absolute z-30 inset-y-0 right-0 w-[88%] max-w-[340px] shadow-xl" : "hidden"} xl:flex xl:relative xl:w-[320px] shrink-0 flex-col border-l border-slate-200 bg-white`}>
            {ctxOpen && (
              <button data-testid="v3-ctx-close" onClick={() => setCtxOpen(false)} className="xl:hidden self-end m-2 rounded-lg border border-slate-200 px-2.5 py-1 text-xs text-slate-500">Cerrar panel</button>
            )}
            <ContextPanelV3 detail={detail} />
          </aside>
          {ctxOpen && <div className="xl:hidden absolute inset-0 z-20 bg-black/40" onClick={() => setCtxOpen(false)} />}
        </div>

        <CloseAlarmModal open={closeOpen} onOpenChange={setCloseOpen} onConfirm={onConfirmClose} submitting={busy} initialNota={notaActual}
          dia1Mode={dia1Cierre?.mode === "person"}
          dia1Clasificacion={dia1Cierre?.clasificacion || ""} dia1Label={dia1Cierre?.label || ""}
          dia1Selectable={dia1Cierre?.selectable || false} notaObligatoria={dia1Cierre?.notaObligatoria || false}
          tecnicoMode={!!detail && esTecnica(detail)}
          instMode={!!dia1Cierre?.inst}
          recordatorioMode={!!dia1Cierre?.recordatorio}
          avisoNuevoEvento={detail?.retrigger_contexto?.amerita_reverificacion
            ? { hora: fmtTimestamp(detail.retrigger_contexto.ultimo_en) } : null}
          onVolverAlCaso={() => setCloseOpen(false)}
          notasPrevias={notasPrevias}
          resolucionesTecnicas={detail?.resoluciones_tecnicas || null} />
        {pDrawer && <PersonaDrawer open onClose={() => setPDrawer(null)} isDemo={isDemo} demoFicha={pDrawer.demoFicha} personaId={isDemo ? null : pDrawer.id} />}
      </div>
    </div>
  );
}
